// Admin session helpers: session validity, logout, and automatic logout on
// expired/rejected sessions. The session itself is an httpOnly cookie; the UI
// only knows who is logged in and when the session ends (lib/storage.js).

import { USER_KEY, SESSION_EXPIRES_KEY, readSessionExpiry, readUser, removeSession } from '../lib/storage';
import { store } from '../store';
import { sessionCleared, sessionSynced } from '../store/authSlice';
import { dashboardReset } from '../store/dashboardSlice';
import { API_BASE, apiUrl, ENDPOINTS } from '../constants/apiEndpoints';

// Candidate-facing pages never require an admin session.
const PUBLIC_PATHS = [/^\/$/, /^\/verify\//, /^\/expired$/];

// Set by installAuthGuard; session checks bypass the guard's own wrapper.
let originalFetch = null;

export function hasValidSession() {
  const expiry = readSessionExpiry();
  return Boolean(readUser()) && expiry !== null && expiry > Date.now();
}

export function clearSession() {
  removeSession();
  store.dispatch(sessionCleared());
  store.dispatch(dashboardReset());
}

// Clears the cookie on the server too; keepalive lets it finish during the redirect.
function endServerSession() {
  (originalFetch || window.fetch)(apiUrl(ENDPOINTS.AUTH_LOGOUT), {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
  }).catch(() => {});
}

// Ends the session in this tab only; the server session is left alone.
function leaveSession() {
  clearSession();
  if (!PUBLIC_PATHS.some((re) => re.test(window.location.pathname))) {
    window.location.replace('/');
  }
}

export function logout() {
  endServerSession();
  leaveSession();
}

let expiryTimer = null;

function scheduleExpiryLogout() {
  clearTimeout(expiryTimer);
  const expiry = readSessionExpiry();
  if (expiry === null) return;
  // setTimeout overflows past ~24.8 days; sessions here last 8h.
  const delay = Math.min(Math.max(expiry - Date.now(), 0), 2147483647);
  expiryTimer = setTimeout(logout, delay);
}

function requestUrl(input) {
  const raw = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
  try {
    return new URL(raw, window.location.href).href;
  } catch {
    return raw;
  }
}

// A 401 from one endpoint isn't proof the session is gone (e.g. an upstream
// failure, or a request that raced a re-login). Ask the server once before logging out.
let sessionCheck = null;

function confirmSessionRejected() {
  if (!sessionCheck) {
    sessionCheck = originalFetch(apiUrl(ENDPOINTS.AUTH_VERIFY), { credentials: 'include' })
      .then((res) => res.status === 401)
      .catch(() => false) // network trouble is not a logout
      .finally(() => { sessionCheck = null; });
  }
  return sessionCheck;
}

export function installAuthGuard() {
  // A confirmed 401 on our own API while logged in ends the session.
  originalFetch = window.fetch.bind(window);
  const apiRoot = API_BASE ? requestUrl(API_BASE) : null;
  const loginUrl = requestUrl(apiUrl(ENDPOINTS.AUTH_LOGIN));
  window.fetch = async (input, init = {}) => {
    const response = await originalFetch(input, init);
    if (response.status !== 401 || !apiRoot || !hasValidSession()) return response;
    const url = requestUrl(input);
    // Wrong password on the login form is not a dead session.
    if (!url.startsWith(apiRoot) || url.startsWith(loginUrl)) return response;

    const expiryAtStart = readSessionExpiry();
    if (await confirmSessionRejected()) {
      // Skip if the user logged in again meanwhile (new session, new expiry).
      if (readSessionExpiry() === expiryAtStart) logout();
    }
    return response;
  };

  // Log out when the session expires, and follow login/logout in other tabs.
  scheduleExpiryLogout();
  window.addEventListener('storage', (e) => {
    if (e.key !== SESSION_EXPIRES_KEY && e.key !== USER_KEY && e.key !== null) return;
    const wasLoggedIn = Boolean(store.getState().auth.user);
    store.dispatch(sessionSynced({ user: readUser(), expiresAt: readSessionExpiry() }));
    if (hasValidSession()) {
      scheduleExpiryLogout();
      return;
    }
    clearTimeout(expiryTimer);
    // Another tab logged out (it already ended the server session): follow it here.
    // A tab that wasn't logged in must stay out of it: a login in another tab writes
    // its keys one at a time, and the half-written session would look invalid.
    if (wasLoggedIn) leaveSession();
  });
}

export { scheduleExpiryLogout };
